<template>
	<div class="chart chart--linechart">
		<svg :id="id" :viewBox="viewBox" preserveAspectRatio="xMinYMin">
			<g id="yAxis">
			</g>
			<g id="xAxis">
			</g>

			<!-- Comments vertical lines -->
			<g v-if="!!this.comments.length" id="commentsLines">
				<line class="commentLine"
				v-for="(comment, c) in refinedComments" :key="'commentline-' + c"
				:x1="x(comment.date)" :x2="x(comment.date)" :y1="margin.top + (comment.offset * size.height)" :y2="y(0)" />
			</g>

			<!-- Areas -->
			<transition-group name="area" id="areas" tag="g">
				<path v-for="area in refinedAreas" class="area" :key="'area-' + area.label" :d="createArea(area.data)" :fill="area.color" />
			</transition-group>	

			<!-- Lines -->		
			<transition-group name="line" tag="g" id="lines" fill="none" :stroke-width="lineWidth" >
				<path class="line" v-for="line in refinedLines" :d="createLine(line.data)" :stroke="line.color" :key="'line-' + line.label" />
			</transition-group>

			<!-- Mouse cursor line -->
			<g v-if="!mobile && showCursorLine" id="cursorLine" style="display: none;">
				<line x1="0" y1="0" x2="0" y2="0" />
				<g id="cursorLabels">
					<text x="80" y="80" class="label label--cursor label--date" id="cursorlabel-date">
						Date
					</text> 
					<text v-for="(line, l) in refinedLines" x="100" y="100" class="label label--cursor label--value" :key="'cursorlabel-' + l" :fill="line.color" :id="'cursorlabel-' + l" ref="cursorlabels">
						00%
					</text> 
				</g>  
			</g>

			<!-- Dots -->
			<transition-group v-if="dotSize > 0" name="dot" tag="g" id="dots">
				<circle class="dot" :class="!dot.hasLine ? 'dot--noLine' : ''" v-for="dot in refinedDots" :r="dotSize" :cx="x(dot.x)" :cy="y(dot.y)" :fill="dot.color" :key="'dot-' + dot.id" @mouseenter="!mobile && showInfoWindow ? toggleInfoWindow('on', dot, $event) : ''" @mouseleave="!mobile && showInfoWindow ? toggleInfoWindow('off') : ''" :data-info="dot.info" :style="{ transitionDelay: !!dot.delay ? dot.delay + 's' : ''}" />
			</transition-group>

			<!-- Dots direct labels -->
			<transition-group v-if="dotDirectLabeling" name="dot-label" tag="g" id="dotsLabels">
				<text class="label label--dot" v-for="dot in refinedDots" :x="x(dot.x)" :y="y(dot.y) - dotDirectLabelingOffset" :key="'dotlabel-' + dot.id" :fill="dot.color" ref="dotLabels" :style="{ transitionDelay: !!dot.delay ? dot.delay + 's' : ''}">
					{{dot.directLabel}}
				</text>
			</transition-group> 

			<!-- Line direct labels on mobile -->
			<template v-if="mobile && showLineLabels">
				<g id="labels">
					<text class="label" v-for="line in refinedLines" :key="'label-' + line.label" :x="size.width - margin.right + 10" :y="y(line.data[line.data.length - 1].y)" :fill="line.color" ref="labels">
						{{!!line.labelShort ? line.labelShort : line.label}}
					</text>
				</g>
			</template>

			<!-- Line direct labels on desktop -->
			<template v-if="!mobile && showLabels && showLineLabels">
				<transition-group id="labels" tag="g" name="label">
					<text class="label" v-for="line in refinedLines" :key="'label-' + line.label" :x="size.width - margin.right + 20" :y="y(line.data[line.data.length - 1].y)" :fill="line.color" ref="labels">
						{{!!line.labelShort ? line.labelShort : line.label}}
					</text>
				</transition-group>
			</template>

			<!-- Y axis label -->
			<g id="units">
				<text v-if="yType != ''" class="unit" x="0" :y="margin.top - (mobile ? 40 : 20)" >
					{{yType}}
				</text>
			</g>


			<!-- Comments texts -->
			<g v-if="!!comments.length" id="commentsTexts">
				<g class="commentTexts" v-for="(comment, c) in refinedComments" :key="'commentline-' + c" >
					<text class="commentText"
					v-for="(line, l) in comment.text" :key="'comment-' + c + '-line-' + l"
					:x="x(comment.date) + (x(comment.date) < size.width / 2 ? 7 : -7)"
					:y="margin.top + (comment.offset * size.height)"
					ref="comments"
					:style="{ textAnchor: x(comment.date) < size.width / 2 ? 'start' : 'end'  }"
					>
						{{line}}
					</text>
				</g>
			</g>
		</svg>

		<!-- Info window -->
		<div v-if="showInfoWindow" id="tooltip" ref="tooltip">
			<div v-if="!!selectedDots.length" class="tooltip__info">
				<div class="tt" v-for="(dot, d) in selectedDots" :key="'dot-' + d" v-html="dot.html"></div>
			</div>
		</div>
	</div>
</template>
<script>
	import { timeFormatLocale, scaleUtc, extent, scaleLinear, axisBottom, axisLeft, min, max, line, area, curveNatural, pointer, bisector, select } from 'd3'
	import { toLocale, clone, preventSvgOverlap } from 'global/js/apps.js'

	export default {
		name: 'Linechart',
		props: {
			size: {
				type: Object,
				default: () => {
					return { width: 800, height: 600 }
				}
			},
			margin: {
				type: Object,
				default: () => {
					return { top: 40, right: 110, bottom: 40, left: 43 }
				},
			},
			rawDates: {
				type: Array,
				default: () => [],
			},
			dateFormat: {
				type: String,
				default: '%d %b %Y',
			},
			dots: {
				type: Array,
				default: () => [],
			},
			dotSize: {
				type: Number,
				default: 5,
			},
			dotDirectLabeling: {
				type: Boolean,
				default: false,
			},
			dotDirectLabelingOffset: {
				type: Number,
				default: 20,
			},
			dotTransitions: {
				type: Boolean,
				default: true,
			},
			lines: {
				type: Array,
				default: () => [],
			},
			areas: {
				type: Array,
				default: () => [],
			},
			lineWidth: {
				type: Number,
				default: 1.5,
			},
			yType: {
				type: String,
				default: '',
			},
			yUnit: {
				type: String,
				default: '',
			},
			showCursorLine: {
				type: Boolean,
				default: false,
			},
			showInfoWindow: {
				type: Boolean,
				default: true,
			},
			showLineLabels: {
				type: Boolean,
				default: true,
			},
			id: {
				type: String,
				default: 'noid-' + parseInt(Math.random() * 10000)
			},
			comments: {
				type: Array,
				default: () => [],
			},
			customYRange: {
				type: Array,
				default: () => [null, null],
			},
			truncateData: {
				type: Boolean,
				default: false,
			},
			nbXTicks: {
				type: Number,
				default: null,
			},
			customXTicks: {
				type: Array,
				default: () => [],
			},
		},
		data: function() {
			return {
				mobile: false,
				breakpoints: {
					mobile: 600,
					smallScreen: 900,
					largeScreen: 1300,
				},
				minHeight: 0,
				svg: null,
				cursorLine: null,
				xAxis: null,
				yAxis: null,
				cursorLabels: [],
				showLabels: true,
				selectedDots: {
					type: Array,
					default: [],
				},
				frDef: {
					'decimal': ',',
					'thousands': '\xa0',
					'grouping': [3],
					'currency': ['', '\xa0€'],
					'dateTime': '%a %b %e %X %Y',
					'date': '%d/%m/%Y',
					'time': '%H:%M:%S',
					'periods': ['AM', 'PM'],
					'days': ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
					'shortDays': ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'],
					'months': ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
					'shortMonths': ['janv.', 'fév.', 'mar.', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
				},
			}
		},
		computed: {
			refinedComments: function() {
				return this.comments.reduce((agg, comment) => {
					return agg.concat([{
						text: comment.text.split('|'),
						date: (comment.date instanceof Date) ? comment.date : new Date(comment.date),
						offset: !!comment.offset ? parseFloat(comment.offset) : 0
					}])
				}, [])
			},
			viewBox: function() {
				return '0, 0, ' + this.size.width + ', ' + this.size.height
			},
			dates: function() {
				return this.rawDates.reduce((agg, rawDate) => {
					return agg.concat(new Date(rawDate)) 
				}, [])
			},
			dateFormatted: function() {
				let fr = timeFormatLocale(this.frDef)
				return fr.format(this.dateFormat) 
			},
			x: function() {
				return scaleUtc()
					.domain(extent(!!this.dates.length ? this.dates : this.allValues.x))
					.range([this.margin.left, this.size.width - this.margin.right])
			},
			y: function() {
				return scaleLinear()
					.domain([this.minYvalue, this.maxYvalue]).nice()
					.range([this.size.height - this.margin.bottom, this.margin.top])
			},
			minYvalue: function() {
				if (!!this.customYRange[0] || this.customYRange[0] == 0) return this.customYRange[0]
				return min(this.allValues.y)
			},
			maxYvalue: function() {
				if (!!this.customYRange[1]) return this.customYRange[1]
				return max(this.allValues.y)
			},
			refinedLines: function() {
				return this.lines.reduce((agg, line) => {
					let refinedLine = clone(line)
					refinedLine.data.map(entry => {
						entry.x = new Date(entry.x)
					})

					if (this.truncateData) refinedLine.data = refinedLine.data.filter(d => d.x >= this.dates[0] && d.x <= this.dates[1])
					if (!refinedLine.data.length) return agg
					return agg.concat(refinedLine)
				}, [])
			},
			refinedAreas: function() {
				return this.areas.reduce((agg, area) => {
					let refinedArea = clone(area)
					refinedArea.data.map(entry => {
						entry.x = new Date(entry.x)
					})
					if (this.truncateData) refinedArea.data = refinedArea.data.filter(d => d.x >= this.dates[0] && d.x <= this.dates[1])
					if (!refinedArea.data.length) return agg
					return agg.concat(refinedArea)
				}, [])
			},
			refinedDots: function() {
				return this.dots.reduce((agg, dot) => {
					let refinedDot = clone(dot)
					let addon = {
						x: new Date(refinedDot.x),
						hasLine: !!this.lines.find(l => l.label == refinedDot.label),
						delay: this.dotTransitions ? parseInt(Math.random()*10)/10 : 0,
					}	
					if (this.truncateData && (addon.x > this.dates[1] || addon.x < this.dates[0])) return agg
					return agg.concat({...dot, ...addon})
				}, [])
			},
			allValues: function() {
				let allValues = {
					x: [],
					y: [],
				}
				Array.from(['refinedLines','refinedAreas']).map(items => {				
					this[items].map(item => {
						item.data.map(entry => {
							allValues.x.push(entry.x)
							if (items == 'refinedLines') allValues.y.push(entry.y)
							if (items == 'refinedAreas') allValues.y.push(entry.max)
						})
					})
				})
				this.dots.map(dot => {
					allValues.x.push(dot.x)
					allValues.y.push(dot.y)
				})
				return	allValues
			}

		},
		methods: {
			createArea: function(data) {
				let dataArea = area()
					.x(d => this.x(d.x))
					.y0(d => this.y(d.min))
					.y1(d => this.y(d.max))
					.curve(curveNatural)

				return dataArea(data)
			},
			createLine: function(data) {
				let dataLine = line()
					.x(d => this.x(d.x))
					.y(d => this.y(d.y))
					.curve(curveNatural)
				
				return dataLine(data)
			},
			initSvg: function() {
				this.svg = select('#' + this.id)

				if (!this.customXTicks.length || !this.nbXTicks) {

					// X axis without custom ticks
					this.xAxis = g => g
						.attr('transform', `translate(0,${this.size.height - this.margin.bottom})`)
						.call(axisBottom(this.x)
							.ticks(Math.round(this.size.width / 130))
							.tickFormat(this.dateFormatted)
							.tickSizeOuter(0)
						)
				} else {

					// X axis with custom ticks
					this.xAxis = g => g
						.attr('transform', `translate(0,${this.size.height - this.margin.bottom})`)
						.call(axisBottom(this.x)
							.ticks(this.nbXTicks)
							.tickValues(this.customXTicks)
							.tickFormat(this.dateFormatted)
							.tickSizeOuter(0)
						)						
				}

				// Y axis
				this.yAxis = g => g
					.attr('transform', `translate(${this.margin.left},0)`)
					.call(axisLeft(this.y)
						.tickFormat(d => d + '\xa0' + this.yUnit )
						.tickSizeInner(- this.size.width + this.margin.right + this.margin.left))
					.call(g => g.select('.domain').remove())

				if (!this.mobile && this.showCursorLine) this.moveCursorLine()
				this.renderSvg()

			},
			renderSvg: function() {
				this.svg.select('#xAxis').call(this.xAxis)
				this.svg.select('#yAxis').call(this.yAxis)
				this.svg.selectAll('#yAxis text').attr('x', -10)
				this.svg.selectAll('#xAxis text').attr('y', +15)

				this.$nextTick(() => {
					setTimeout(() => {
						if (!!this.$refs.labels) preventSvgOverlap(this.$refs.labels)
						if (!!this.$refs.comments) preventSvgOverlap(this.$refs.comments, false, 1)
						if (!!this.$refs.dotLabels) preventSvgOverlap(this.$refs.dotLabels)
					}, 300)
				})
			},
			isPointerInChart: function(event) {

				return pointer(event)[0] > this.margin.left && pointer(event)[0] < this.size.width - this.margin.right
			},
			moveCursorLine: function() {
				this.cursorLine = this.svg.select('#cursorLine')
				this.svg.on('mousemove', event => {

					if (this.isPointerInChart(event) && !this.selectedDots.length) {
						let x = pointer(event)[0]
						
						let xValue = this.x.invert(x)
					// 	console.log(x, this.size.width, this.margin.right)
						this.showLabels = x < this.size.width - this.margin.right - 60
						
						this.cursorLine.select('line')
						.attr('x1', x+5)
						.attr('y1', this.y(0))
						.attr('x2', x+5)
						.attr('y2', this.y(this.y.domain()[1]))

						this.refinedLines.map((line, l) => {
							let bisect = bisector(function(d) { return d.x }).right
							let match = line.data[bisect(line.data, xValue)]
							if (!!match) {
								let value = match.y
								this.cursorLine.select('#cursorlabel-' + l)
								.attr('x', x + 13)
								.attr('y', this.y(value) - 5)
								.text(toLocale(value, 3) + '\xa0%')
							}
						})
						this.cursorLine.select('#cursorlabel-date')
						.attr('x', x + 13)
						.attr('y', this.y(this.y.domain()[1]) + 20)
						.text(xValue.toLocaleString('fr-FR', { day: 'numeric', month: 'long' }))
						this.cursorLine.style('display', 'block')
						this.$nextTick( () => {

							preventSvgOverlap(this.$refs.cursorlabels)
						})
					} else {
						this.cursorLine.style('display', 'none')
						this.showLabels = true
					}
				})
				.on('mouseout', () => {
					this.svg.select('#cursorLine').style('display', 'none')
				})
			},
			toggleInfoWindow: function(onOff, dot, evt) {
				if (onOff == 'on') {
					this.selectedDots = []
					this.refinedDots.map(d => {
						if (d.x.getTime() == dot.x.getTime() && d.y == dot.y) {
							this.selectedDots.push(d)
						}
					})
					this.$refs.tooltip.style.display = 'block'
					this.$refs.tooltip.style.top =  evt.offsetY + 20 + 'px'
					this.$refs.tooltip.style.left = evt.offsetX - 20 + 'px'
					this.$nextTick(function() {
						this.minHeight = this.$refs.tooltip.getBoundingClientRect().bottom + document.body.scrollTop
					})
					
				}
				if (onOff == 'off') {
					this.selectedDots = []
					this.$refs.tooltip.style.display = '' 
					this.minHeight = 0
				}

			},
		},
		watch: {
			lines: function() {
				if (!!this.svg) this.renderSvg()
			},
			dates: function() {
				if (!!this.svg) this.renderSvg()
			},
			minHeight: function() {
				this.$emit('resize', this.minHeight)
			},
			showLabels: function(value) {
				if (value) this.$nextTick(() => {
					preventSvgOverlap(this.$refs.labels)
				})
			}
		},
		created() {
			if (this.innerWidth <= this.breakpoints.mobile) this.mobile = true
		},
		mounted() {
			this.$nextTick(function() {
				this.initSvg()
			})
		},
	}
</script>
<style lang="scss" >
	@import 'global/css/contexte.apps.colors';
	@import 'global/css/contexte.apps.fonts';
	.chart {
		position: relative;
	}
	#xAxis, #yAxis {
		path, line {
			stroke: $light-grey;
		}
		text {
			fill: $medium-grey;
		}
	}
	#cursorLine {
		line {
			stroke: $another-grey;
			stroke-width: .7px;
			stroke-dasharray: 5 5;
		}
	}
	#xAxis {
		text {
			font-size: 14px;
		}
	}
	#yAxis {
		text {
			font-size: 14px;
		}
	}
	.unit {	
		font-size: 14px;
	}
	.label {
		font-family: $sans;
		transform-origin: bottom;
		font-size: 14px;
		alignment-baseline: central;
		transition: 
			transform .3s ease-in-out,
			opacity .3s linear;
		&-enter, &-leave-to {
			transform: scaleY(0);
			opacity: 0;
		}
		&--masked {
			opacity: .2;
		}
		&--cursor:not(.label--date) {
			font-size: 18px;
			font-weight: 600;
		}
		&--date {
			font-size: 12px;
			font-weight: 400;
		}
		&--dot {
			text-anchor: middle;
			font-size: 12px;
			transition-property: opacity;
			transition-timing-function: linear;
			transition-duration: .3s;
		}
	}
	.line {
		transform-origin: bottom;
		transform: scaleY(1);
		transition: 
			transform .3s ease-in-out,
			opacity .3s linear;
		&-enter, &-leave-to {
			transform: scaleY(0);
			opacity: 0;
		}
		&--masked {
			opacity: .2;
		}
	}
	.dot {
		cursor: auto;
		fill-opacity: .3;
		cursor: default;
		transition-property: opacity;
		transition-timing-function: linear;
		transition-duration: .3s;
		&--noLine {
			transition: none;
			fill-opacity: 1;
		}
		&-enter, &-leave-to {
			opacity: 0;
		}
		&--masked {
			opacity: .2;
		}
	}
	.area {
		stroke: none;
		fill-opacity: .1;
		transition: opacity .6s linear .3s;
		&-enter, &-leave-to {
			opacity: 0;
		}
		&--masked {
			opacity: .2;
		}
	}
	.hoverable {
		fill: rgba(1,1,1,0);
	}
	#tooltip {
		position: absolute;
		display: none;
		background-color: white;
		padding: .4em;
		border: 1px solid $dark-grey;
		max-width: 175px;
		z-index: 2;
	}
	.tt {
		margin-bottom: 7px;
		&:last-child {
			margin-bottom: 0;
		}
		&:not(:first-child) {		
			.tt__score {
				display: none;
			}
		}
		&__score {
			font-weight: 600;
			font-size: 1.2em;
			margin-bottom: .2em;
		}
		&__candidate {
			font-size: .9em;
			margin-bottom: .1em;
		}
		&__details {
			color: $medium-grey;
			font-size:  .7em;
		}
	}
	.comment {
		&Text {
			font-size: 13px;
			fill: $medium-grey;
			dominant-baseline: hanging;
		}
		&Line {
			stroke: $light-grey;
			stroke-width: .6px;
		}
	}
	@media screen and (min-width: 1200px)  {
		.comment {
			&Text {
				font-size: 11px;
				fill: $medium-grey;
				dominant-baseline: hanging;
			}
		}
	}
	@media screen and (max-width: 600px)  {
		#xAxis text, #yAxis text {
			font-size: 28px;
		}
		.unit {
			font-size: 30px;
		}
		.label {
			font-size: 30px;
			transition: none;

			&-enter, &-leave-to {
				opacity: 1;
			}
		}
		.comment {
			&Text {
				font-size: 24px;
			}
		}
	}
</style>



